import { useNavigate, useLocation } from "react-router-dom";
import { 
  Sparkles, LayoutDashboard, FolderOpen, Calendar, Settings, 
  Mic, ChevronLeft, ChevronRight, Home, LogOut
} from "lucide-react";
import { Button } from "./ui/button";
import { useState } from "react";
import { cn } from "../lib/utils";
import { toast } from "sonner";

const NAV_ITEMS = [
  { id: "voice", label: "Voice Command", icon: Mic, path: null, action: "voice" },
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/dashboard" },
  { id: "content", label: "Content Library", icon: FolderOpen, path: "/content" },
  { id: "calendar", label: "Calendar", icon: Calendar, path: "/calendar" },
  { id: "settings", label: "Settings", icon: Settings, path: "/settings" },
];

export default function Sidebar({ active, onVoiceCommand }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const currentPath = location.pathname;

  const handleNavClick = (item) => {
    if (item.action === "voice" && onVoiceCommand) {
      onVoiceCommand();
    } else if (item.path) {
      navigate(item.path);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    toast.success("Logged out successfully");
    navigate("/");
  };

  const handleGoHome = () => {
    navigate("/");
  };

  return (
    <aside className={cn(
      "hidden md:flex flex-col h-screen fixed top-0 left-0 glass border-r border-white/5 transition-all duration-300 z-40",
      collapsed ? "w-20" : "w-64"
    )}>
      {/* Logo */}
      <div className={cn(
        "flex items-center gap-3 p-6 border-b border-white/5",
        collapsed && "justify-center"
      )}>
        <img 
          src="/logo.png" 
          alt="YASNO" 
          className="w-10 h-10 rounded-xl object-contain flex-shrink-0 cursor-pointer"
          onClick={handleGoHome}
        />
        {!collapsed && (
          <span className="text-lg font-bold tracking-tight cursor-pointer" onClick={handleGoHome}>
            <span className="text-white">YAS</span>
            <span className="text-primary">NO</span>
          </span>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-2">
        {NAV_ITEMS.map((item) => {
          const isActive = currentPath === item.path || active === item.id;
          return (
            <Button
              key={item.id}
              variant="ghost"
              className={cn(
                "w-full justify-start gap-3 h-12 transition-all",
                isActive && "bg-primary/10 text-primary border border-primary/20",
                item.action === "voice" && "bg-gradient-to-r from-primary/20 to-purple-500/20 border border-primary/30 hover:from-primary/30 hover:to-purple-500/30",
                collapsed && "justify-center px-0"
              )}
              onClick={() => handleNavClick(item)}
              data-testid={`nav-${item.id}`}
            >
              <item.icon className={cn(
                "w-5 h-5 flex-shrink-0", 
                isActive && "text-primary",
                item.action === "voice" && "text-primary"
              )} />
              {!collapsed && <span>{item.label}</span>}
            </Button>
          );
        })}
      </nav>

      {/* Home & Logout Buttons */}
      <div className="p-4 space-y-2 border-t border-white/5">
        <Button
          variant="ghost"
          className={cn(
            "w-full justify-start gap-3 h-10 text-muted-foreground hover:text-foreground",
            collapsed && "justify-center px-0"
          )}
          onClick={handleGoHome}
          data-testid="nav-home"
        >
          <Home className="w-5 h-5 flex-shrink-0" />
          {!collapsed && <span>Back to Home</span>}
        </Button>
        
        <Button
          variant="ghost"
          className={cn(
            "w-full justify-start gap-3 h-10 text-red-400 hover:text-red-300 hover:bg-red-500/10",
            collapsed && "justify-center px-0"
          )}
          onClick={handleLogout}
          data-testid="nav-logout"
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {!collapsed && <span>Logout</span>}
        </Button>
      </div>

      {/* Collapse Toggle */}
      <div className="p-4 border-t border-white/5">
        <Button
          variant="ghost"
          size="icon"
          className="w-full h-10"
          onClick={() => setCollapsed(!collapsed)}
          data-testid="sidebar-toggle"
        >
          {collapsed ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <ChevronLeft className="w-5 h-5" />
          )}
        </Button>
      </div>
    </aside>
  );
}
